/**
 * Production-ready Axios retry queue system
 * - Network offline detection & request queue
 * - Single reconnect listener (no duplicate listeners)
 * - Max 3 retries per request, exponential backoff for network errors only
 * - Configurable non-retry status codes (400, 401, 403, 404, 409, 422, 429)
 * - Proper cancellation handling
 * - Safe memory cleanup
 * - MAX_QUEUE_SIZE to prevent unbounded memory growth when offline
 * - SSR-safe (server treated as online)
 * - Default request timeout to prevent hanging requests
 * - 401 handling with token refresh and cross-tab coordination
 */

import axios from "axios";
import { refreshAccessToken } from "../Utils/tokenManager";
import logger from "../Utils/logger";

// ---------------------------------------------------------------------------
// Configuration (adjust at top of file)
// ---------------------------------------------------------------------------
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const DEFAULT_TIMEOUT_MS = 15000;
const MAX_QUEUE_SIZE = 100;

const NO_RETRY_STATUSES = [400, 401, 403, 404, 409, 422, 429];

const RETRY_STATUSES = [500, 502, 503, 504];

const NETWORK_ERROR_CODES = [
	"ECONNABORTED",
	"ETIMEDOUT",
	"ECONNREFUSED",
	"ECONNRESET",
	"ENETUNREACH",
	"ERR_NETWORK",
	"ERR_FAILED",
	"ERR_INTERNET_DISCONNECTED",
];

// ---------------------------------------------------------------------------
// Singleton state
// ---------------------------------------------------------------------------
let onlineListenerAttached = false;
let onlineHandlerRef = null;
const requestQueue = [];
let isProcessingQueue = false;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isOffline = () =>
	typeof navigator === "undefined" ? false : !navigator.onLine;

const isNetworkError = (error) => {
	if (!error) return false;
	if (error.code && NETWORK_ERROR_CODES.includes(error.code)) return true;
	if (
		error.message &&
		/network|timeout|connection|failed|fetch/i.test(error.message)
	)
		return true;
	if (!error.response) return true;
	return false;
};

const shouldNotRetryByStatus = (status) =>
	status != null && NO_RETRY_STATUSES.includes(status);

const isRetryableStatus = (status) =>
	status != null && RETRY_STATUSES.includes(status);

const isCancelled = (error) => axios.isCancel(error);

const getBackoffDelay = (retryIndex) =>
	Math.min(BASE_DELAY_MS * Math.pow(2, retryIndex), MAX_BACKOFF_MS);

const attachOnlineListener = () => {
	if (onlineListenerAttached || typeof window === "undefined") return;

	onlineHandlerRef = () => {
		if (isOffline()) return;
		processQueue();
	};

	window.addEventListener("online", onlineHandlerRef);
	onlineListenerAttached = true;
};

export const cleanup = () => {
	if (
		!onlineListenerAttached ||
		!onlineHandlerRef ||
		typeof window === "undefined"
	)
		return;

	window.removeEventListener("online", onlineHandlerRef);

	onlineListenerAttached = false;
	onlineHandlerRef = null;

	requestQueue.length = 0;
};

const processQueue = async () => {
	if (isOffline() || isProcessingQueue || requestQueue.length === 0) return;

	isProcessingQueue = true;

	while (requestQueue.length > 0 && !isOffline()) {
		const item = requestQueue.shift();

		if (!item) continue;

		try {
			const result = await item.executor();
			item.resolve(result);
		} catch (err) {
			item.reject(err);
		}
	}

	isProcessingQueue = false;
};

const enqueue = (executor) =>
	new Promise((resolve, reject) => {
		if (requestQueue.length >= MAX_QUEUE_SIZE) {
			reject(
				new Error(
					`Offline queue full (max ${MAX_QUEUE_SIZE}). Request dropped.`
				)
			);
			return;
		}

		requestQueue.push({ executor, resolve, reject });
	});

// ---------------------------------------------------------------------------
// Axios instance with retry & queue
// ---------------------------------------------------------------------------
const createRetryAxiosInstance = () => {
	const instance = axios.create({
		timeout: DEFAULT_TIMEOUT_MS,
	});

	// -----------------------------------------------------------------------
	// Response interceptor
	// -----------------------------------------------------------------------
	instance.interceptors.response.use(
		(response) => response,

		async (error) => {
			// =========================
			// HANDLE 401 (refresh + retry once)
			// =========================
			const config = error.config || {};
			if (error.response?.status === 401 && !config._retried) {
				config._retried = true;
				try {
					logger.log('[AxiosInterceptor] 401 error, attempting token refresh...');
					const newToken = await refreshAccessToken();
					config.headers = config.headers || {};
					config.headers.Authorization = `Bearer ${newToken}`;
					logger.log('[AxiosInterceptor] Token refreshed, retrying request...');
					return instance.request(config);
				} catch (refreshErr) {
					logger.error('[AxiosInterceptor] Token refresh failed:', refreshErr.message);
					// Token refresh failed (auth error or no valid token) - reject the original 401
					return Promise.reject(error);
				}
			}

			if (isCancelled(error)) return Promise.reject(error);

			const currentRetryCount = config.__retryCount ?? 0;

			const status = error.response?.status;

			if (shouldNotRetryByStatus(status))
				return Promise.reject(error);

			const shouldRetry =
				isNetworkError(error) || isRetryableStatus(status);

			if (!shouldRetry) return Promise.reject(error);

			const nextRetryCount = currentRetryCount + 1;

			if (nextRetryCount > MAX_RETRIES)
				return Promise.reject(error);

			config.__retryCount = nextRetryCount;

			const delay = getBackoffDelay(nextRetryCount - 1);

			await new Promise((r) => setTimeout(r, delay));

			return instance.request(config);
		}
	);

	// -----------------------------------------------------------------------
	// Offline queue wrapper
	// -----------------------------------------------------------------------
	const originalRequest = instance.request.bind(instance);

	instance.request = function request(config) {
		attachOnlineListener();

		if (isOffline()) {
			return enqueue(() => instance.request(config));
		}

		return originalRequest(config);
	};

	return instance;
};

export const retryAxios = createRetryAxiosInstance();

export default retryAxios;